"use client";
import * as React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Minus, ShoppingCart, Trash2, CreditCard, Banknote, Smartphone, Wallet, Search } from "lucide-react";
import { useERP, formatBDT, formatNum, type ID } from "@/lib/erp/store";
import { useToast } from "@/hooks/use-toast";

type PayMode = "Cash" | "Card" | "bKash" | "Nagad";
interface CartItem { productId: ID; name: string; price: number; qty: number; }

export function POSModule() {
  const erp = useERP();
  const { toast } = useToast();
  const [query, setQuery] = React.useState("");
  const [category, setCategory] = React.useState("All");
  const [cart, setCart] = React.useState<CartItem[]>([]);
  const [customerId, setCustomerId] = React.useState<ID>(erp.customers[0]?.id ?? "");
  const [payMode, setPayMode] = React.useState<PayMode>("Cash");
  const [posSales, setPosSales] = React.useState<{ id: string; customer: string; total: number; mode: PayMode; date: string }[]>([]);

  const categories = ["All", ...Array.from(new Set(erp.products.map(p => p.category)))];
  const filteredProducts = erp.products.filter(p =>
    (category === "All" || p.category === category) &&
    (p.name.toLowerCase().includes(query.toLowerCase()) || p.sku.toLowerCase().includes(query.toLowerCase()))
  );

  const addToCart = (p: typeof erp.products[number]) => {
    setCart(c => {
      const ex = c.find(i => i.productId === p.id);
      if (ex) return c.map(i => i.productId === p.id ? { ...i, qty: i.qty + 1 } : i);
      return [...c, { productId: p.id, name: p.name, price: p.salePrice, qty: 1 }];
    });
  };

  const updateQty = (id: ID, delta: number) => {
    setCart(c => c.map(i => i.productId === id ? { ...i, qty: Math.max(1, i.qty + delta) } : i));
  };

  const removeItem = (id: ID) => setCart(c => c.filter(i => i.productId !== id));

  const subtotal = cart.reduce((s, i) => s + i.price * i.qty, 0);
  const tax = subtotal * 0.15;
  const total = subtotal + tax;

  const checkout = () => {
    if (cart.length === 0) { toast({ title: "Empty cart", variant: "destructive" }); return; }
    const soId = erp.createSO({
      customerId,
      warehouseId: erp.warehouses[0].id,
      orderDate: new Date().toISOString().slice(0, 10),
      requestedDelivery: new Date().toISOString().slice(0, 10),
      lines: cart.map(i => ({ productId: i.productId, qty: i.qty, unitPrice: i.price, discount: 0, taxRate: 15 })),
      notes: `POS Sale — ${payMode}`,
    } as never);
    const invId = erp.createInvoice(soId);
    if (invId) erp.postInvoice(invId, "POS Cashier");
    const rctId = erp.createReceipt({ customerId, receiptDate: new Date().toISOString().slice(0, 10), amount: total, paymentMode: payMode === "Cash" ? "Cash" : payMode === "Card" ? "Bank Transfer" : payMode, allocations: invId ? [{ invoiceId: invId, amount: total }] : [] } as never);
    if (rctId) erp.reconcileReceipt(rctId);
    const customer = erp.getCustomer(customerId);
    setPosSales(s => [{ id: soId, customer: customer?.name ?? "—", total, mode: payMode, date: new Date().toISOString().slice(0, 10) }, ...s]);
    setCart([]);
    toast({ title: "Sale completed", description: `Total ${formatBDT(total)} via ${payMode}` });
  };

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-xl font-semibold sm:text-2xl">Retail POS</h1>
        <p className="text-muted-foreground mt-1 text-sm">Fast checkout with auto SO → Invoice → Receipt creation.</p>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* Product grid */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <CardTitle>Products</CardTitle>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <Select value={category} onValueChange={setCategory}>
                    <SelectTrigger className="w-full sm:w-40"><SelectValue /></SelectTrigger>
                    <SelectContent>{categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                  <div className="relative w-full sm:w-56">
                    <Search className="text-muted-foreground absolute left-2.5 top-1/2 size-4 -translate-y-1/2" />
                    <Input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search product..." className="pl-8" />
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
                {filteredProducts.map(p => (
                  <button key={p.id} onClick={() => addToCart(p)}
                    className="flex flex-col gap-1 rounded-md border p-3 text-left transition-colors hover:border-primary hover:bg-muted/40">
                    <div className="text-xs font-mono text-muted-foreground">{p.sku}</div>
                    <div className="line-clamp-2 text-sm font-medium leading-tight">{p.name}</div>
                    <div className="mt-1 flex items-center justify-between">
                      <span className="text-primary font-semibold">{formatBDT(p.salePrice)}</span>
                      <Badge variant="secondary" className="text-[10px]">{p.category}</Badge>
                    </div>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Cart */}
        <div>
          <Card className="sticky top-4">
            <CardHeader><CardTitle className="flex items-center gap-2"><ShoppingCart className="size-5" /> Cart ({cart.length})</CardTitle></CardHeader>
            <CardContent className="flex flex-col gap-3">
              <Select value={customerId} onValueChange={setCustomerId}>
                <SelectTrigger className="w-full"><SelectValue placeholder="Select customer..." /></SelectTrigger>
                <SelectContent>{erp.customers.map(c => <SelectItem key={c.id} value={c.id}>{c.code} — {c.name}</SelectItem>)}</SelectContent>
              </Select>

              <div className="omega-sidebar-scroll max-h-72 overflow-y-auto border rounded-md">
                {cart.length === 0 ? (
                  <p className="text-muted-foreground p-4 text-center text-sm">Cart is empty. Click products to add.</p>
                ) : cart.map(i => (
                  <div key={i.productId} className="flex items-center gap-2 border-b p-2 last:border-0">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">{i.name}</p>
                      <p className="text-muted-foreground text-xs">{formatBDT(i.price)} × {i.qty}</p>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button size="icon" variant="outline" className="size-7" onClick={() => updateQty(i.productId, -1)}><Minus className="size-3" /></Button>
                      <span className="w-6 text-center text-sm">{i.qty}</span>
                      <Button size="icon" variant="outline" className="size-7" onClick={() => updateQty(i.productId, 1)}><Plus className="size-3" /></Button>
                      <Button size="icon" variant="ghost" className="text-destructive size-7" onClick={() => removeItem(i.productId)}><Trash2 className="size-3" /></Button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="space-y-1 border-t pt-2 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span className="font-mono">{formatBDT(subtotal)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">VAT (15%)</span><span className="font-mono">{formatBDT(tax)}</span></div>
                <div className="flex justify-between border-t pt-1 text-base font-semibold"><span>Total</span><span className="font-mono">{formatBDT(total)}</span></div>
              </div>

              <div>
                <p className="mb-1.5 text-xs font-medium">Payment Method</p>
                <div className="grid grid-cols-2 gap-1.5">
                  {([["Cash", Banknote], ["Card", CreditCard], ["bKash", Smartphone], ["Nagad", Wallet]] as const).map(([m, Icon]) => (
                    <Button key={m} size="sm" variant={payMode === m ? "default" : "outline"} onClick={() => setPayMode(m)}>
                      <Icon className="size-4" /> {m}
                    </Button>
                  ))}
                </div>
              </div>

              <Button className="w-full" disabled={cart.length === 0} onClick={checkout}>
                Checkout · {formatBDT(total)}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Recent POS sales */}
      <Card>
        <CardHeader><CardTitle>Recent POS Sales</CardTitle></CardHeader>
        <CardContent>
          {posSales.length === 0 ? (
            <p className="text-muted-foreground py-6 text-center text-sm">No POS sales yet. Complete a checkout to see it here.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50"><tr className="border-b">
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase">SO ID</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase">Customer</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase">Date</th>
                  <th className="px-3 py-2 text-right text-xs font-semibold uppercase">Total</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase">Mode</th>
                </tr></thead>
                <tbody>
                  {posSales.map(s => (
                    <tr key={s.id} className="border-b last:border-0">
                      <td className="px-3 py-2 font-mono text-xs">{s.id.slice(0, 8)}</td>
                      <td className="px-3 py-2 font-medium">{s.customer}</td>
                      <td className="px-3 py-2">{s.date}</td>
                      <td className="px-3 py-2 text-right font-mono">{formatBDT(s.total)}</td>
                      <td className="px-3 py-2"><Badge variant="secondary">{s.mode}</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
